import { Box, TextField } from '@mui/material';
import { FormikErrors, FormikProps, FormikTouched } from 'formik';
import React, { useCallback, useMemo } from 'react';

import { CANADA, USA } from '../../constants/countries';
import {
  RECEIVER_ADDRESS1,
  RECEIVER_ADDRESS2,
  RECEIVER_CITY,
  RECEIVER_COMPANY_NAME,
  RECEIVER_COUNTRY,
  RECEIVER_STATE,
  RECEIVER_ZIP,
} from '../../constants/shipping';
import useWindowSize from '../../hooks/useWindowSize';
import { IAddressInfo } from '../../types/api';
import { ISelectorValue } from '../../types/shell';
import { IAddressSuggestion, ICustomOrFixedAddressForm } from '../../types/shipping';
import { AutocompleteInput } from '../_mui/_components_/AutocompleteInput';
import { CountrySelect as MuiCountrySelect } from '../_mui/_components_/CountrySelect';

interface IProps {
  form: FormikProps<ICustomOrFixedAddressForm>;
  onSelect: (suggestion: IAddressSuggestion) => void;
  onReset?: () => void;
  allowedCountries?: string[];
}

const AddressForm = ({ form, onSelect, allowedCountries }: IProps) => {
  const { mobile } = useWindowSize();

  const values = form.values.userAddress;
  const errors =
    typeof form.errors.userAddress === 'object' ? form.errors.userAddress : ({} as FormikErrors<IAddressInfo>);
  const touched =
    typeof form.touched.userAddress === 'object' ? form.touched.userAddress : ({} as FormikTouched<IAddressInfo>);
  const prefix = 'userAddress.';

  const onChange = <T, _>(f: string, v: T, shouldValidate?: boolean) =>
    form.setFieldValue(`userAddress.${f}`, v, shouldValidate);

  const isUSA = useMemo(() => values?.[RECEIVER_COUNTRY] === USA.two_digit_code, [values?.[RECEIVER_COUNTRY]]);
  const isStateRequired = useMemo(
    () => values?.[RECEIVER_COUNTRY] === USA.two_digit_code || values?.[RECEIVER_COUNTRY] === CANADA.two_digit_code,
    [values?.[RECEIVER_COUNTRY]],
  );

  const handleCountrySelect = useCallback(
    (country: ISelectorValue | null) => {
      // Touched changes validate the values from the last render, which still hold the old country;
      // only the setFieldValue below validates the new one.
      if (Object.keys(form.errors).length) {
        form.setTouched({}, false);
        form.setErrors({});
      }

      onChange(RECEIVER_COUNTRY, country?.value || '');
    },
    [onChange, form.errors, form.setTouched, form.setErrors],
  );

  const handleCountryBlur = useCallback(
    () => form.setFieldTouched(`${prefix}${RECEIVER_COUNTRY}`, true, false),
    [form.setFieldTouched],
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.value && Object.keys(form.errors).length) {
        form.setTouched({});
        form.setErrors({});
      }
      form.handleChange(e);
    },
    [form.handleChange, form.setTouched, form.errors],
  );

  return (
    <>
      <TextField
        label="Company name"
        placeholder="Company name"
        name={`${prefix}${RECEIVER_COMPANY_NAME}`}
        onChange={handleChange}
        onBlur={form.handleBlur}
        error={touched?.[RECEIVER_COMPANY_NAME] ? !!errors?.[RECEIVER_COMPANY_NAME] : false}
        value={values?.[RECEIVER_COMPANY_NAME] || ''}
        fullWidth
        size="medium"
        helperText={touched?.[RECEIVER_COMPANY_NAME] ? errors?.[RECEIVER_COMPANY_NAME] : undefined}
      />
      <MuiCountrySelect
        name={RECEIVER_COUNTRY}
        value={values?.[RECEIVER_COUNTRY]}
        allowedCountries={allowedCountries}
        onChange={handleCountrySelect}
        onBlur={handleCountryBlur}
        error={touched?.[RECEIVER_COUNTRY] ? errors?.[RECEIVER_COUNTRY] : undefined}
        label="Country"
        required
        helperText={touched?.[RECEIVER_COUNTRY] ? errors?.[RECEIVER_COUNTRY] : undefined}
      />
      {isUSA ? (
        <AutocompleteInput
          label="Address Line 1"
          isRequired
          name={`${prefix}${RECEIVER_ADDRESS1}`}
          onChange={handleChange}
          onBlur={form.handleBlur}
          error={touched?.[RECEIVER_ADDRESS1] ? errors?.[RECEIVER_ADDRESS1] : undefined}
          value={values?.[RECEIVER_ADDRESS1]}
          onSelect={onSelect}
        />
      ) : (
        <TextField
          size="medium"
          required
          name={`${prefix}${RECEIVER_ADDRESS1}`}
          label="Address Line 1"
          placeholder="Address Line 1"
          onChange={handleChange}
          onBlur={form.handleBlur}
          error={touched?.[RECEIVER_ADDRESS1] ? !!errors?.[RECEIVER_ADDRESS1] : false}
          value={values?.[RECEIVER_ADDRESS1] || ''}
          fullWidth
          helperText={touched?.[RECEIVER_ADDRESS1] ? errors?.[RECEIVER_ADDRESS1] : undefined}
        />
      )}
      <TextField
        size="medium"
        name={`${prefix}${RECEIVER_ADDRESS2}`}
        label="Address Line 2"
        placeholder="Address Line 2"
        onChange={handleChange}
        onBlur={form.handleBlur}
        value={values?.[RECEIVER_ADDRESS2] || ''}
        fullWidth
        helperText={touched?.[RECEIVER_ADDRESS2] ? errors?.[RECEIVER_ADDRESS2] : undefined}
      />
      <Box sx={{ display: 'flex', flexDirection: mobile ? 'column' : 'row', gap: mobile ? 3 : 2 }}>
        <TextField
          size="medium"
          required
          name={`${prefix}${RECEIVER_CITY}`}
          label="City"
          placeholder="City"
          error={touched?.[RECEIVER_CITY] ? !!errors?.[RECEIVER_CITY] : false}
          onChange={handleChange}
          onBlur={form.handleBlur}
          value={values?.[RECEIVER_CITY] || ''}
          fullWidth
          helperText={touched?.[RECEIVER_CITY] ? errors?.[RECEIVER_CITY] : undefined}
        />
        <TextField
          size="medium"
          required={isStateRequired}
          name={`${prefix}${RECEIVER_STATE}`}
          label="State / Region"
          placeholder="State / Region"
          onChange={handleChange}
          onBlur={form.handleBlur}
          error={touched?.[RECEIVER_STATE] ? !!errors?.[RECEIVER_STATE] : false}
          value={values?.[RECEIVER_STATE] || ''}
          fullWidth
          helperText={touched?.[RECEIVER_STATE] ? errors?.[RECEIVER_STATE] : undefined}
        />
        <TextField
          size="medium"
          required
          name={`${prefix}${RECEIVER_ZIP}`}
          label="ZIP Code"
          placeholder="ZIP Code"
          onChange={handleChange}
          onBlur={form.handleBlur}
          error={touched?.[RECEIVER_ZIP] ? !!errors?.[RECEIVER_ZIP] : false}
          value={values?.[RECEIVER_ZIP] || ''}
          fullWidth
          helperText={touched?.[RECEIVER_ZIP] ? errors?.[RECEIVER_ZIP] : undefined}
        />
      </Box>
    </>
  );
};

export default AddressForm;
